import { Controller } from "@hotwired/stimulus";
import { startExport, pollExportStatus } from "../components/DownloadTray/downloadFlow";

/**
 * Stimulus controller for triggering async report exports.
 *
 * Usage in HTML:
 *   <button data-controller="async-export"
 *           data-async-export-report-exports-url-value="/admin/events/my-event/report_exports"
 *           data-async-export-grid-type-value="report"
 *           data-async-export-export-type-value="csv"
 *           data-async-export-report-id-value="123"
 *           data-action="click->async-export#export">
 *     Export CSV
 *   </button>
 */
export class AsyncExportController extends Controller {
  static values = {
    reportExportsUrl: String,
    gridType: String,
    exportType: String,
    reportId: Number,
    filters: Object,
    params: Object,
  };

  // Find the actual button element (Primer may wrap it)
  get buttonElement() {
    return this.element.tagName === "BUTTON"
      ? this.element
      : this.element.querySelector("button") || this.element;
  }

  async export(event) {
    event.preventDefault();

    const button = this.buttonElement;

    // Prevent multiple clicks
    if (button.disabled || button.hasAttribute("aria-busy")) {
      return;
    }

    // Disable button and show loading state
    button.disabled = true;
    button.setAttribute("aria-busy", "true");

    try {
      const params = {};
      if (this.hasParamsValue && this.paramsValue) {
        Object.assign(params, this.paramsValue);
      }
      if (this.hasFiltersValue && this.filtersValue) {
        params.f = this.filtersValue;
      }

      const result = await startExport(
        this.reportExportsUrlValue,
        this.gridTypeValue,
        this.exportTypeValue,
        params,
        this.hasReportIdValue ? this.reportIdValue : undefined,
        {
          onConfirm: (exportType) => {
            // Dispatch event to show confirmation dialog
            window.dispatchEvent(
              new CustomEvent("showDownloadConfirmation", {
                detail: { exportType },
              })
            );
          },
        }
      );

      // Start polling for completion if export started successfully
      if (result?.id) {
        pollExportStatus(
          this.reportExportsUrlValue,
          result.id,
          this.exportTypeValue,
          this.gridTypeValue
        );
      }
    } catch (error) {
      console.error("[AsyncExportController] Export failed:", error);
    } finally {
      // Always re-enable button after export starts (status will be shown in DownloadTray)
      // Use setTimeout to ensure state is properly reset
      setTimeout(() => {
        button.disabled = false;
        button.removeAttribute("aria-busy");
      }, 100);
    }
  }
}
